import React from 'react'
import { baseUrl } from '@fs/zion-config'
import { i18n } from '@fs/zion-locale'
import { useTranslation } from 'react-i18next'
import Iframe from 'react-iframe'
import Head, { PageHead } from '@fs/zion-head'
import { useOverlay, DialogOverlay, DialogOverlayActions, Button, Paragraph, Separator } from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import { Usabilla } from '@fs/zion-usabilla'
import { css } from '@emotion/core'
import { useFeatureFlagWithPrefs, useFeatureFlag } from '@fs/zion-flags'
import oldEnglandMapsImage from './OldEnglandMaps.webp'

const fullscreenContainerStyle = css`
  position: fixed;
  top: 0;
  inset-inline-start: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`

const fullscreenImageStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (max-width: 768px) {
    object-position: center;
  }
`

function EnglandMapsAppPageHead({ title, description, canonicalUrl }) {
  return (
    <>
      <PageHead includeHreflang title={title} description={description} url={canonicalUrl} />
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
    </>
  )
}

function ShowEnglandJurisdictions() {
  const [t] = useTranslation()

  // Determine which ArcGIS experience URL to use based on baseUrl
  const arcgisUrl = baseUrl.includes('beta.familysearch.org')
    ? 'https://experience.arcgis.com/experience/ecfbb152f70546389f8b6d88dbd5b051'
    : 'https://experience.arcgis.com/experience/a0ca526b8be34c408f23d0f5fd99dfda'

  return (
    <>
      <Usabilla side="right" url="//w.usabilla.com/5c534b30ae2a.js" />
      <EnglandMapsAppPageHead
        title={t('england-maps.page.title', 'England Maps')}
        description={t('england-maps-page.page.description', 'FamilySearch England Maps')}
        canonicalUrl={`${baseUrl}/${i18n.language}/standards/england-maps/`}
      />
      <Iframe
        url={arcgisUrl}
        styles={{ border: 'none', height: 'calc(100vh - 62px)', width: '100%' }}
        title="England Maps"
      />
    </>
  )
}

export default function EnglandMapsPage() {
  // Move feature flag hooks to the top level to fix the conditional hooks error
  const { data, loading } = useFeatureFlagWithPrefs(
    'standards_places_englandJurisdictions',
    {},
    { userPreferences: ['fslabs.labs'] }
  )
  const showLabsPopup = useFeatureFlag('standards_places_showLabsPopup')?.isOn
  const showPopup = useFeatureFlag('standards_places_showPopup')?.isOn

  // Remove unused t variable
  const overlay = useOverlay()
  const initData = { page_detail: 'FamilySearch England Maps' }
  usePageView(initData)

  const showEnglandJurisdictions = !!data?.isOn

  if (loading) {
    return null
  }

  if (showEnglandJurisdictions) {
    return <ShowEnglandJurisdictions />
  }

  // Show dialog if either flag is true
  if (showLabsPopup || showPopup) {
    // Determine which message to show based on flags
    let dialogMessage
    let dialogTitle
    let showLabsButton = false

    if (showLabsPopup) {
      dialogTitle = 'Try England Jurisdictions 1851 in Labs'
      dialogMessage = `England Jurisdictions 1851 is temporarily offline for system upgrades
      starting Friday, May 23rd, 2025. A pre-release version is available in FamilySearch Labs. 
      We appreciate your patience during this maintenance period.`
      showLabsButton = true
    } else {
      dialogTitle = 'Update'
      dialogMessage = `England Jurisdictions 1851 is temporarily offline
      for system upgrades starting Friday, May 23rd, 2025. We're enhancing the
      experience to better serve your research needs. The service will be restored
      as soon as possible, and we'll provide updates on this page as work progresses.
      We appreciate your patience during this maintenance period.`
    }

    return (
      <>
        <div
          css={fullscreenContainerStyle}
          onClick={overlay.handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              overlay.handleClick()
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Open dialog"
        >
          <img src={oldEnglandMapsImage} alt="Old England Maps" css={fullscreenImageStyle} />
        </div>

        <DialogOverlay size="md" title={dialogTitle} {...overlay}>
          <Paragraph size="sm">{dialogMessage}</Paragraph>
          <Separator size="md" />
          <DialogOverlayActions>
            <Button emphasis={showLabsButton ? 'low' : 'high'} onClick={overlay.close}>
              Close
            </Button>
            {showLabsButton && (
              <Button emphasis="high" to={`${baseUrl}/${i18n.language}/labs/`}>
                Go to Labs Experience
              </Button>
            )}
          </DialogOverlayActions>
        </DialogOverlay>
      </>
    )
  }

  return <ShowEnglandJurisdictions />
}
